import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Loader2, UserCheck } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyJamaah = {
  full_name: '', nik: '', passport_number: '', date_of_birth: '', gender: 'male',
  address: '', phone: '', email: '', emergency_contact_name: '', emergency_contact_phone: ''
};

const CheckoutPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [loadingCart, setLoadingCart] = useState(true);
  const [cartItems, setCartItems] = useState([]);
  const [settings, setSettings] = useState({ ppn_enabled: false, ppn_rate: 1.0, dp_type: 'percent', dp_percent: 30.0, dp_value: 0.0 });
  const [pkgDpEnabled, setPkgDpEnabled] = useState(false);
  const [pkgVoucherEnabled, setPkgVoucherEnabled] = useState(false);
  const [voucherCode, setVoucherCode] = useState('');
  const [voucherInfo, setVoucherInfo] = useState(null);
  const [voucherLoading, setVoucherLoading] = useState(false);
  const [voucherError, setVoucherError] = useState('');
  const [paymentType, setPaymentType] = useState('full'); // full | dp
  const [savedJamaahs, setSavedJamaahs] = useState([]);
  const [jamaahData, setJamaahData] = useState([{ ...emptyJamaah }]);

  useEffect(() => {
    const fetchCart = async () => {
      try {
        const res = await axios.get(`${API}/cart/`);
        setCartItems(res.data);
        if (res.data.length === 0) {
          toast.error('Keranjang kosong');
          navigate('/cart');
        } else {
          // fetch package type settings (dp_enabled / voucher_enabled) from master
          const first = res.data[0];
          const pkgId = first.package_id || first.package?.id;
          if (pkgId && pkgId !== 'custom') {
            try {
              const pk = await axios.get(`${API}/packages/${pkgId}`);
              const ptypeCode = pk.data.package_type;
              // fetch package type master
              const typesRes = await axios.get(`${API}/master/package-types`);
              const pt = typesRes.data.find(t => t.code === ptypeCode);
              const dpEnabled = pt ? pt.dp_enabled : pk.data.dp_enabled;
              const voucherEnabled = pt ? pt.voucher_enabled : pk.data.voucher_enabled;
              setPkgDpEnabled(!!dpEnabled);
              setPkgVoucherEnabled(!!voucherEnabled);
              setPaymentType(dpEnabled ? 'dp' : 'full');
            } catch {}
          }
        }
      } catch {
        toast.error('Gagal memuat data keranjang');
        navigate('/cart');
      } finally {
        setLoadingCart(false);
      }
    };
    const fetchSettings = async () => {
      try {
        const res = await axios.get(`${API}/settings/`);
        setSettings({
          ppn_enabled: res.data.ppn_enabled ?? false,
          ppn_rate: res.data.ppn_rate ?? 1.0,
          dp_type: res.data.dp_type ?? 'percent',
          dp_percent: res.data.dp_percent ?? 30.0,
          dp_value: res.data.dp_value ?? 0.0
        });
      } catch {}
    };
    const fetchSavedJamaahs = async () => {
      try {
        const res = await axios.get(`${API}/cart/jamaah/mine`);
        setSavedJamaahs(res.data);
      } catch {}
    };
    fetchCart();
    fetchSettings();
    fetchSavedJamaahs();
  }, [navigate]);

  const handleJamaahChange = (index, field, value) => {
    const updated = [...jamaahData];
    updated[index] = { ...updated[index], [field]: value };
    setJamaahData(updated);
  };

  const handlePickJamaah = (index, e) => {
    const id = e.target.value;
    if (!id) {
      setJamaahData(prev => { const u = [...prev]; u[index] = { ...emptyJamaah }; return u; });
      return;
    }
    const found = savedJamaahs.find(j => j.id === id || j.nik === id);
    if (found) {
      setJamaahData(prev => {
        const u = [...prev];
        u[index] = {
          full_name: found.full_name || '',
          nik: found.nik || '',
          passport_number: found.passport_number || '',
          date_of_birth: found.date_of_birth || '',
          gender: found.gender || 'male',
          address: found.address || '',
          phone: found.phone || '',
          email: found.email || '',
          emergency_contact_name: found.emergency_contact_name || '',
          emergency_contact_phone: found.emergency_contact_phone || ''
        };
        return u;
      });
      toast.success(`Data ${found.full_name} terisi otomatis`);
    }
  };

  const handleAddJamaah = () => {
    setJamaahData([...jamaahData, { ...emptyJamaah }]);
  };

  const handleRemoveJamaah = (index) => {
    setJamaahData(prev => prev.filter((_, i) => i !== index));
  };

  const calculateTotal = () => {
    return cartItems.reduce((total, item) => {
      const price = item.custom_package?.price || item.package?.price || 0;
      return total + (price * (item.quantity || 1));
    }, 0);
  };

  const validateVoucher = async () => {
    if (!voucherCode.trim()) { setVoucherInfo(null); setVoucherError(''); return; }
    setVoucherLoading(true); setVoucherError('');
    try {
      const res = await axios.post(`${API}/vouchers/validate`, { code: voucherCode.trim() });
      setVoucherInfo(res.data);
    } catch (err) {
      setVoucherInfo(null);
      setVoucherError(err.response?.data?.detail || 'Voucher tidak valid');
    } finally {
      setVoucherLoading(false);
    }
  };

  const subtotalCalc = calculateTotal();
  const taxCalc = settings.ppn_enabled ? subtotalCalc * (settings.ppn_rate / 100) : 0;
  const voucherDisc = voucherInfo?.valid
    ? (voucherInfo.type === 'percent'
        ? subtotalCalc * (voucherInfo.value / 100)
        : Math.min(voucherInfo.value, subtotalCalc))
    : 0;
  const totalCalc = subtotalCalc + taxCalc - voucherDisc;
  const dpCalc = paymentType === 'dp'
    ? (settings.dp_type === 'nominal'
        ? Math.min(Math.round(settings.dp_value || 0), totalCalc)
        : Math.round(totalCalc * (settings.dp_percent / 100)))
    : 0;
  const remainingCalc = paymentType === 'dp' ? totalCalc - dpCalc : 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (jamaahData.some(j => !j.full_name || !j.nik || !j.date_of_birth || !j.address || !j.phone || !j.email)) {
      toast.error('Lengkapi semua data jamaah wajib');
      return;
    }
    if (cartItems.length === 0) {
      toast.error('Keranjang kosong');
      return;
    }

    setLoading(true);
    try {
      const firstItem = cartItems[0];
      const packageId = firstItem.package_id || firstItem.package?.id || 'custom';

      const checkoutRes = await axios.post(`${API}/checkout/`, {
        package_id: packageId,
        total_pilgrims: jamaahData.length,
        jamaahs: jamaahData,
        notes: '',
        voucher_code: voucherInfo?.valid ? voucherInfo.code : '',
        payment_type: paymentType
      });

      toast.success('Pesanan berhasil dibuat! Silakan lakukan pembayaran transfer.');
      navigate(`/order/${checkoutRes.data.order_id}`);
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error('Gagal memproses checkout: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  if (loadingCart) {
    return (
      <div className="min-h-screen py-12 bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  const formatPrice = (p) => `Rp ${(p || 0).toLocaleString('id-ID')}`;

  return (
    <div className="min-h-screen py-12 bg-background">
      <div className="container mx-auto px-4 max-w-4xl">
        <h1 className="text-4xl font-bold font-['Outfit'] mb-8">Checkout</h1>

        <form onSubmit={handleSubmit}>
          {/* Ringkasan Pesanan */}
          <Card className="rounded-2xl mb-6">
            <CardHeader>
              <CardTitle>Ringkasan Pesanan</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {cartItems.map((item) => {
                const pkg = item.custom_package || item.package || {};
                return (
                  <div key={item.id} className="flex justify-between text-sm">
                    <span>{pkg.package_name || 'Paket Umrah Mandiri'} × {item.quantity || 1}</span>
                    <span className="font-medium">{formatPrice((pkg.price || 0) * (item.quantity || 1))}</span>
                  </div>
                );
              })}
            </CardContent>
          </Card>

          {/* Jamaah Data */}
          <div className="space-y-6 mb-8">
            {jamaahData.map((jamaah, index) => (
              <Card key={index} className="rounded-2xl">
                <CardHeader>
                  <div className="flex justify-between items-center">
                    <CardTitle>Data Jamaah {index + 1}</CardTitle>
                    {jamaahData.length > 1 && (
                      <Button type="button" variant="destructive" size="sm" onClick={() => handleRemoveJamaah(index)}>
                        Hapus
                      </Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  {/* Saved jamaah picker */}
                  {savedJamaahs.length > 0 && (
                    <div className="mb-4 p-3 bg-muted/50 rounded-lg">
                      <Label className="flex items-center gap-2 mb-2">
                        <UserCheck className="h-4 w-4" /> Pilih Jamaah Tersimpan
                      </Label>
                      <select
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                        value=""
                        onChange={(e) => handlePickJamaah(index, e)}
                      >
                        <option value="">— Jamaah Baru —</option>
                        {savedJamaahs.map((j) => (
                          <option key={j.nik || j.id} value={j.nik || j.id}>
                            {j.full_name} — NIK: {j.nik} — {j.email}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Nama Lengkap *</Label>
                      <Input value={jamaah.full_name} onChange={(e) => handleJamaahChange(index, 'full_name', e.target.value)} required />
                    </div>
                    <div className="space-y-2">
                      <Label>NIK *</Label>
                      <Input value={jamaah.nik} onChange={(e) => handleJamaahChange(index, 'nik', e.target.value)} required />
                    </div>
                    <div className="space-y-2">
                      <Label>No. Paspor</Label>
                      <Input value={jamaah.passport_number} onChange={(e) => handleJamaahChange(index, 'passport_number', e.target.value)} />
                    </div>
                    <div className="space-y-2">
                      <Label>Tanggal Lahir *</Label>
                      <Input type="date" value={jamaah.date_of_birth} onChange={(e) => handleJamaahChange(index, 'date_of_birth', e.target.value)} required />
                    </div>
                    <div className="space-y-2">
                      <Label>Jenis Kelamin *</Label>
                      <select className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" value={jamaah.gender} onChange={(e) => handleJamaahChange(index, 'gender', e.target.value)}>
                        <option value="male">Laki-laki</option>
                        <option value="female">Perempuan</option>
                      </select>
                    </div>
                    <div className="space-y-2">
                      <Label>No. Telepon *</Label>
                      <Input value={jamaah.phone} onChange={(e) => handleJamaahChange(index, 'phone', e.target.value)} required />
                    </div>
                    <div className="space-y-2 md:col-span-2">
                      <Label>Email *</Label>
                      <Input type="email" value={jamaah.email} onChange={(e) => handleJamaahChange(index, 'email', e.target.value)} required />
                    </div>
                    <div className="space-y-2 md:col-span-2">
                      <Label>Alamat *</Label>
                      <Input value={jamaah.address} onChange={(e) => handleJamaahChange(index, 'address', e.target.value)} required />
                    </div>
                    <div className="space-y-2">
                      <Label>Kontak Darurat - Nama</Label>
                      <Input value={jamaah.emergency_contact_name} onChange={(e) => handleJamaahChange(index, 'emergency_contact_name', e.target.value)} />
                    </div>
                    <div className="space-y-2">
                      <Label>Kontak Darurat - Telepon</Label>
                      <Input value={jamaah.emergency_contact_phone} onChange={(e) => handleJamaahChange(index, 'emergency_contact_phone', e.target.value)} />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <Button type="button" variant="outline" className="w-full mb-8 rounded-full" onClick={handleAddJamaah}>
            + Tambah Jamaah Lain
          </Button>

          {/* Voucher + Payment Summary */}
          <Card className="rounded-2xl mb-8">
            <CardHeader>
              <CardTitle>Ringkasan Pembayaran</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Voucher */}
              {pkgVoucherEnabled && (
              <div className="space-y-2">
                <Label>Kode Voucher (opsional)</Label>
                <div className="flex gap-2">
                  <Input
                    value={voucherCode}
                    onChange={(e) => { setVoucherCode(e.target.value); setVoucherInfo(null); setVoucherError(''); }}
                    placeholder="Masukkan kode voucher"
                  />
                  <Button type="button" variant="outline" onClick={validateVoucher} disabled={voucherLoading}>
                    {voucherLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Cek'}
                  </Button>
                </div>
                {voucherInfo?.valid && (
                  <p className="text-sm text-green-600">Voucher valid! Diskon {voucherInfo.type === 'percent' ? voucherInfo.value + '%' : formatPrice(voucherInfo.value)}</p>
                )}
                {voucherError && <p className="text-sm text-red-500">{voucherError}</p>}
              </div>
              )}

              {/* Payment type */}
              {pkgDpEnabled && (
              <div className="space-y-2">
                <Label>Metode Pembayaran</Label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setPaymentType('full')}
                    className={`flex-1 rounded-xl border p-3 text-sm font-medium transition-colors ${paymentType === 'full' ? 'border-primary bg-primary/10' : 'border-border'}`}
                  >
                    Bayar Lunas
                  </button>
                  <button
                    type="button"
                    onClick={() => setPaymentType('dp')}
                    className={`flex-1 rounded-xl border p-3 text-sm font-medium transition-colors ${paymentType === 'dp' ? 'border-primary bg-primary/10' : 'border-border'}`}
                  >
                    DP {settings.dp_type === 'nominal' ? `Rp ${Number(settings.dp_value || 0).toLocaleString('id-ID')}` : `${settings.dp_percent}%`} 
                  </button>
                </div>
              </div>
              )}

              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Subtotal</span>
                <span>{formatPrice(subtotalCalc)}</span>
              </div>
              {settings.ppn_enabled && (
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">PPN ({settings.ppn_rate}%)</span>
                  <span>{formatPrice(taxCalc)}</span>
                </div>
              )}
              {voucherDisc > 0 && (
                <div className="flex justify-between text-sm text-green-600">
                  <span>Diskon Voucher</span>
                  <span>-{formatPrice(voucherDisc)}</span>
                </div>
              )}
              <div className="border-t pt-3 flex justify-between font-bold text-lg">
                <span>Total {paymentType === 'dp' ? '(DP)' : ''}</span>
                <span>{formatPrice(paymentType === 'dp' ? dpCalc : totalCalc)}</span>
              </div>
              {paymentType === 'dp' && (
                <p className="text-sm text-muted-foreground">Sisa pelunasan: {formatPrice(remainingCalc)}</p>
              )}
            </CardContent>
          </Card>

          <Button type="submit" className="w-full rounded-full" size="lg" disabled={loading}>
            {loading ? <Loader2 className="h-5 w-5 animate-spin mr-2" /> : null}
            Buat Pesanan — {formatPrice(paymentType === 'dp' ? dpCalc : totalCalc)}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default CheckoutPage;
